import { useCallback, useEffect, useRef, useState } from "react";
import { getJob, listJobs } from "../api/jobs";
import { isTerminalStatus, type ConversionJob } from "../types/job";
import { JOB_POLL_INTERVAL_MS } from "../utils/constants";

interface UseJobsResult {
    jobs: ConversionJob[];
    loading: boolean;
    error: string | null;
    addJob: (job: ConversionJob) => void;
    removeJob: (jobId: string) => void;
    refresh: () => Promise<void>;
}

/**
 * Owns the guest's job list and polls only the jobs that are still active.
 * Polling for a given job starts as soon as it appears (from an upload or
 * from the initial GET /jobs on load/reload) and stops the moment it
 * reaches a terminal status - never polls a completed/failed/rejected/
 * expired job forever.
 */
export function useJobs(ready: boolean): UseJobsResult {
    const [jobs, setJobs] = useState<ConversionJob[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const stopPolling = useCallback((jobId: string) => {
        const timer = timers.current.get(jobId);
        if (timer !== undefined) {
            clearTimeout(timer);
            timers.current.delete(jobId);
        }
    }, []);

    const pollJob = useCallback(
        (jobId: string) => {
            if (timers.current.has(jobId)) return;

            const tick = () => {
                getJob(jobId)
                    .then((updated) => {
                        setJobs((prev) => prev.map((j) => (j.job_id === jobId ? updated : j)));
                        if (isTerminalStatus(updated.status)) {
                            stopPolling(jobId);
                            return;
                        }
                        timers.current.set(jobId, setTimeout(tick, JOB_POLL_INTERVAL_MS));
                    })
                    .catch(() => {
                        // Transient network error: keep the last known state and retry.
                        timers.current.set(jobId, setTimeout(tick, JOB_POLL_INTERVAL_MS));
                    });
            };
            timers.current.set(jobId, setTimeout(tick, JOB_POLL_INTERVAL_MS));
        },
        [stopPolling],
    );

    const refresh = useCallback(async () => {
        try {
            const list = await listJobs();
            setJobs(list);
            setError(null);
        } catch {
            setError("Impossibile recuperare le tue conversioni. Riprova.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!ready) return;
        void refresh();
    }, [ready, refresh]);

    // Keep polling in sync with the current job list: start for any active
    // job (including ones restored after a page reload), stop for any that
    // became terminal.
    useEffect(() => {
        const activeIds = new Set(jobs.filter((j) => !isTerminalStatus(j.status)).map((j) => j.job_id));
        for (const id of activeIds) pollJob(id);
        for (const id of Array.from(timers.current.keys())) {
            if (!activeIds.has(id)) stopPolling(id);
        }
    }, [jobs, pollJob, stopPolling]);

    useEffect(() => {
        const timersMap = timers.current;
        return () => {
            timersMap.forEach((timer) => clearTimeout(timer));
            timersMap.clear();
        };
    }, []);

    const addJob = useCallback((job: ConversionJob) => {
        setJobs((prev) => [job, ...prev]);
    }, []);

    const removeJob = useCallback(
        (jobId: string) => {
            stopPolling(jobId);
            setJobs((prev) => prev.filter((j) => j.job_id !== jobId));
        },
        [stopPolling],
    );

    return { jobs, loading, error, addJob, removeJob, refresh };
}
